import React from "react";
import PropTypes from "prop-types";
import axios from "axios";
import { AlertCircle, Button, DotsVertical, Edit01, Menu, Select } from "@opendate-app/setlist";
import SeatingChartCard from "../../ConfirmTicketing/components/SeatingChartCard";

const errorMessage = (e) =>
  (e && e.response && e.response.data && e.response.data.error) || "Could not load that chart's sections.";

// The confirm's chart picker: a grid of chart cards until one is chosen, then
// the chosen chart in a Select with a ⋮ menu. The series edits the venue's
// chart itself (every date uses it), so "Edit Seating Chart" opens the venue's
// chart settings rather than a per-event copy.
const SeatingChartPicker = ({
  charts,
  value,
  sectionsUrl,
  context,
  showTypeLabel,
  editChartUrl,
  title = "Select Seating Chart",
  description = "The chart's sections define your ticket types and capacities.",
  swapWarning = "Choosing another chart resets this show type's section prices, seat holds and kills.",
  onPick,
  onChooseVenue,
}) => {
  const [error, setError] = React.useState(null);

  const pick = (chartId) => {
    setError(null);
    return axios
      .get(sectionsUrl, { params: { seating_chart_id: chartId, ...(context && { context }) } })
      .then(({ data }) => onPick(chartId, data.sections || []))
      .catch((e) => setError(errorMessage(e)));
  };

  if (charts.length === 0) {
    return (
      <div className="series-notice warning" data-testid="no-seating-charts">
        <AlertCircle width={20} height={20} aria-hidden="true" className="series-notice-icon" />
        <p className="series-notice-body">
          This venue has no seating charts. Add one in venue settings, or{" "}
          <button type="button" className="series-notice-link" onClick={onChooseVenue}>
            choose another venue
          </button>
          .
        </p>
      </div>
    );
  }

  return (
    <section className="series-seating-chart-picker">
      <div className="d-flex justify-content-between align-items-start" style={{ gap: 12 }}>
        <div>
          <h3 className="series-seating-chart-title">{title}</h3>
          <p className="series-edit-hint">
            {value
              ? "Ticket types are defined by your seating configuration. You can modify pricing for each ticket type."
              : `Choose a seating configuration${showTypeLabel ? ` for ${showTypeLabel} shows` : " for this series"}. ${description}`}
          </p>
        </div>
        {value && (
          <div className="d-flex align-items-center" style={{ gap: 8 }}>
            <Select
              size="sm"
              width={200}
              aria-label={context ? "Add-on seating chart" : "Seating chart"}
              options={charts.map((chart) => ({ value: chart.id, label: chart.name }))}
              value={value}
              onChange={(chartId) => chartId && chartId !== value && pick(chartId)}
            />
            {editChartUrl && (
              <Menu
                placement="bottom-end"
                trigger={<Button variant="tertiary" size="sm" icon={DotsVertical} aria-label="Seating chart actions" />}
                items={[{ label: "Edit Seating Chart", icon: Edit01, onClick: () => window.open(editChartUrl, "_blank", "noopener") }]}
              />
            )}
          </div>
        )}
      </div>
      {!value && (
        <div className="form-row" style={{ marginTop: "14px" }} data-testid={context ? "add-on-chart-cards" : "chart-cards"}>
          {charts.map((chart) => (
            <SeatingChartCard
              key={chart.id}
              seatingChart={{ id: chart.id, name: chart.name, preview_thumb_url: chart.previewThumbUrl }}
              csrfToken=""
              isAddOn={Boolean(context)}
              onSelectChart={() => pick(chart.id)}
            />
          ))}
        </div>
      )}
      {value && (
        <p className="series-edit-hint" data-testid="chart-swap-warning">
          {swapWarning}
        </p>
      )}
      {error && <p className="series-schedule-error">{error}</p>}
    </section>
  );
};

SeatingChartPicker.propTypes = {
  charts: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number, name: PropTypes.string, previewThumbUrl: PropTypes.string })).isRequired,
  value: PropTypes.number,
  sectionsUrl: PropTypes.string.isRequired,
  context: PropTypes.string,
  showTypeLabel: PropTypes.string,
  editChartUrl: PropTypes.string,
  title: PropTypes.string,
  description: PropTypes.string,
  swapWarning: PropTypes.string,
  onPick: PropTypes.func.isRequired,
  onChooseVenue: PropTypes.func.isRequired,
};

export default SeatingChartPicker;
